import h from 'hyperscript';

import bookmarkIcon from '@disy/cadenza-icons/bookmark.svg';
import moreIcon from '@disy/cadenza-icons/more.svg';
import plusIcon from '@disy/cadenza-icons/plus.svg';
import { compareDates } from '@disy/lib/date-comparator';
import { formatDate } from '@disy/lib/format';
import { logger } from '@disy/lib/logging';
import { openUrl } from '@disy/lib/open-url';
import { type Column, DataProvider, ResponsiveTable } from '@disy/ui.responsive-table/responsive-table';
import { createActionsColumn } from '@disy/ui.responsive-table/responsive-table-actions';
import { defaultRowRenderer } from '@disy/ui.responsive-table/responsive-table-row-renderer';
import { createButton } from '@disy/ui/button';
import { copyToClipboard } from '@disy/ui/clipboard-button';
import { icon } from '@disy/ui/icon';
import { LabelWithIcon } from '@disy/ui/label-with-icon';
import { closeAllPopups } from '@disy/ui/popup';
import { showToastMessage } from '@disy/ui/toast-message';

import { AlertDialog } from 'ui/dialog/alert/alert-dialog';
import { KeyValuePanel } from 'ui/key-value-panel/key-value-panel';
import { createToggletip } from 'ui/toggletip/toggletip';

import {
  createPublicWorkbookUrl,
  createWorkbookSnapshot,
  deleteWorkbooks,
  getWorkbookSnapshots,
  updateWorkbookSnapshot,
} from 'cadenza/api-client/workbook-management-api';
import type { WorkbookId } from 'cadenza/repository/model/workbook-id.gen';
import { getStore } from 'cadenza/store/cadenza-store';
import type { WorkbookSnapshotDto } from 'cadenza/workbook/api/workbook-snapshot-dto';
import { saveMapViewsExtentSilently } from 'cadenza/workbook/workbook-core-actions.state';
import {
  getWorkbookId,
  getWorkbookNameAndHash,
  getWorkbookRelation,
  getWorkbookType,
  isWorkbookSaved,
  isWorkbookVersionPublishedWhenRepositoryPublishingProcessEnabled,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { updateWorkbookPrintName } from 'cadenza/workbook/workbook.state';

import './share-menu-interim-status-content.css';
import i18n from './share-menu-interim-status-content.properties';

export function createPersonalInterimStatusNewButton(tableProvider: () => ResponsiveTable<WorkbookSnapshotDto>) {
  return createButton([icon(plusIcon, { variant: 'normal' }), h('span', i18n('createButton.label'))], {
    title: i18n('createButton.label'),
    styleClass: 'd-menu-item',
    disabled: !(
      isWorkbookSaved(getStore().getState()) &&
      isWorkbookVersionPublishedWhenRepositoryPublishingProcessEnabled(getStore().getState())
    ),
    onclick: () => createSnapshotDialog(tableProvider),
  });
}

export function createPersonalInterimStatusTable() {
  let table: ResponsiveTable<WorkbookSnapshotDto>;
  table = new ResponsiveTable<WorkbookSnapshotDto>({
    rowRenderer: (options) => {
      const tr = defaultRowRenderer(options);
      tr.classList.toggle('current-snapshot', isCurrentWorkbook(options.rowData.workbookId));
      return tr;
    },
    columns: [createMainColumn(), createMoreActionsColumn(() => table)],
    noResultsMessage: '',
    data: DataProvider.staticData(() => createPersonalInterimStatusTableData()),
    showHeader: false,
    paginationPageSize: 10,
  });
  return table;
}

async function createPersonalInterimStatusTableData() {
  const snapshots = await getWorkbookSnapshots(getWorkbookOrSnapshotId());
  return snapshots.sort((a, b) => {
    return compareDates(stringToDate(b.createdOn), stringToDate(a.createdOn));
  });
}

function createToggletipHeader(printName: string) {
  const header = new LabelWithIcon(icon(bookmarkIcon));
  header.textContent = printName;
  return h('h1', header);
}

function createToggletipContent(rowData: WorkbookSnapshotDto) {
  const createdOn = rowData.createdOn;
  const basedOn = rowData.parentWorkbookIdAndVersion.version;
  const contentEntries = [
    {
      key: i18n('createdOn'),
      value: createdOn ? formatDate(new Date(createdOn)) : '-',
    },
    {
      key: i18n('basedOn'),
      value: basedOn ? `v${basedOn}` : '-',
    },
  ];
  if (rowData.description) {
    contentEntries.push({
      key: i18n('description'),
      value: rowData.description,
    });
  }
  const scrollPanel = new KeyValuePanel(contentEntries);
  scrollPanel.classList.add('interim-status-details');
  return scrollPanel;
}

function createMainColumn(): Column<WorkbookSnapshotDto> {
  return {
    key: 'printName',
    renderer: (printName: string, { rowData }) => {
      return h(`.d-stack-h.space-1`, [
        createSnapshotLink(printName, rowData),
        createMainColumnSnapshotToggletip(printName, rowData),
      ]);
    },
  };
}

function createMainColumnSnapshotToggletip(printName: string, rowData: WorkbookSnapshotDto) {
  return createToggletip({
    size: 'xs',
    toggletipStyleClass: ['d-stack-v', 'space-3'],
    toggletipContent: () => [createToggletipHeader(printName), createToggletipContent(rowData)],
  });
}

function createSnapshotLink(printName: string, rowData: WorkbookSnapshotDto) {
  const title = new LabelWithIcon(icon(bookmarkIcon));
  title.textContent = printName;
  title.classList.add('ellipsis');
  return h(
    'a.status-link',
    { href: getSnapshotUrl(rowData), title: i18n('action.openSnapshot'), target: '_blank' },
    title,
  );
}

function createDeleteSnapshotAction(
  rowData: WorkbookSnapshotDto,
  tableProvider: () => ResponsiveTable<WorkbookSnapshotDto>,
) {
  return {
    label: i18n('action.deleteSnapshot'),
    execute: (event: MouseEvent) => {
      deleteWorkbooks([rowData.workbookId])
        // refresh even if we redirect, otherwise the table keeps showing the "just deleted" row until the page redirects
        .then(() => tableProvider().refresh())
        .then(() => possiblyRedirectOnDeletion(rowData))
        .catch(() =>
          showToastMessage(i18n('action.deleteSnapshot.failure', { workbookPrintName: rowData.printName }), {
            type: 'error',
          }),
        );
      // Prevent closing share menu when deleting one snapshot from the table. But close the submenu.
      event.stopPropagation();
      closeAllPopups((p) => p.contains(event.target as HTMLElement));
    },
  };
}

function createCopySnapshotLinkToClipboardAction(rowData: WorkbookSnapshotDto) {
  return {
    label: i18n('action.copySnapshotLinkToClipboard'),
    execute: (event: MouseEvent) => {
      copyToClipboard(getSnapshotUrl(rowData).toString(), {
        toast: i18n('action.copySnapshotLinkToClipboard.success'),
      });
      event.stopPropagation();
      closeAllPopups((p) => p.contains(event.target as HTMLElement));
    },
  };
}

function createOpenInterimStatusAction(rowData: WorkbookSnapshotDto) {
  return {
    label: i18n('action.openSnapshot'),
    execute: () => openUrl(getSnapshotUrl(rowData).toString()),
  };
}

function createUpdateSnapshotAction(rowData: WorkbookSnapshotDto) {
  return {
    label: i18n('action.updateSnapshot'),
    disabled: !(
      isWorkbookSaved(getStore().getState()) &&
      isWorkbookVersionPublishedWhenRepositoryPublishingProcessEnabled(getStore().getState())
    ),
    execute: () => showUpdateSnapshotConfirmDialog(rowData),
  };
}

function createEditSnapshotAction(
  rowData: WorkbookSnapshotDto,
  tableProvider: () => ResponsiveTable<WorkbookSnapshotDto>,
) {
  return {
    label: i18n('action.editSnapshot'),
    execute: (event: MouseEvent) => {
      event.stopPropagation();
      closeAllPopups((p) => p.contains(event.target as HTMLElement));
      editSnapshotDialog(rowData, tableProvider);
    },
  };
}

function createMoreActionsColumn(
  tableProvider: () => ResponsiveTable<WorkbookSnapshotDto>,
): Column<WorkbookSnapshotDto> {
  return createActionsColumn(1, ({ rowData }) => [
    {
      icon: icon(moreIcon),
      label: i18n('moreActions'),
      subMenu: () => {
        if (isCurrentWorkbook(rowData.workbookId)) {
          return [
            createOpenInterimStatusAction(rowData),
            createCopySnapshotLinkToClipboardAction(rowData),
            createUpdateSnapshotAction(rowData),
            createEditSnapshotAction(rowData, tableProvider),
            createDeleteSnapshotAction(rowData, tableProvider),
          ];
        } else {
          return [
            createCopySnapshotLinkToClipboardAction(rowData),
            createEditSnapshotAction(rowData, tableProvider),
            createDeleteSnapshotAction(rowData, tableProvider),
          ];
        }
      },
    },
  ]);
}

function getWorkbookOrSnapshotId() {
  switch (getWorkbookType(getStore().getState())) {
    case 'WORKBOOK':
      return getWorkbookId(getStore().getState());
    case 'SNAPSHOT':
      return getWorkbookRelation(getStore().getState())!.parent;
    default:
      throw new Error('Type ' + getWorkbookType(getStore().getState()) + ' is not mapped, yet');
  }
}

async function createSnapshotDialog(tableProvider: () => ResponsiveTable<WorkbookSnapshotDto>) {
  const { CreateWorkbookSnapshotDialog } =
    await import('cadenza/workbook/header/create-workbook-snapshot-dialog/create-workbook-snapshot-dialog');
  const workbookSnapshotData = await new CreateWorkbookSnapshotDialog().connectAndShow();
  if (workbookSnapshotData) {
    try {
      await saveMapViewsExtentSilently();
      // reset hash after extent command
      workbookSnapshotData.workbookHash = getWorkbookNameAndHash(getStore().getState()).hash;
      const currentWorkbookId = getWorkbookId(getStore().getState());
      const { workbookId, worksheetId } = await createWorkbookSnapshot(currentWorkbookId, workbookSnapshotData);
      const createdSnapshotUrl = new URL(createPublicWorkbookUrl(workbookId, worksheetId), location.origin).toString();
      copyToClipboard(createdSnapshotUrl, { showToast: false });
      await tableProvider().refresh();
    } catch (error) {
      logger.log('Failed to create workbook snapshot!', error);
    }
  }
}

function showUpdateSnapshotConfirmDialog(snapshotDto: WorkbookSnapshotDto) {
  return new AlertDialog(i18n('action.updateSnapshot.warning.message'), {
    type: 'warning',
    submit: i18n('action.updateSnapshot.warning.confirm'),
    submitCallback: () => updateSnapshot(snapshotDto),
    cancel: true,
  }).connectAndShow();
}

/**
 * Updating a snapshot is creating a new one, deletes the current one and redirects to the new one.
 */
async function updateSnapshot(snapshotDto: WorkbookSnapshotDto) {
  try {
    const workbookSnapshotDto = await createWorkbookSnapshot(snapshotDto.workbookId, {
      workbookHash: getWorkbookNameAndHash(getStore().getState()).hash,
      printName: snapshotDto.printName,
      worksheetId: snapshotDto.worksheetId,
      description: snapshotDto.description,
    });
    await deleteWorkbooks([snapshotDto.workbookId]);
    redirect(getSnapshotUrl(workbookSnapshotDto).toString());
  } catch {
    showToastMessage(i18n('action.updateSnapshot.failure', { workbookPrintName: snapshotDto.printName }));
  }
}

function possiblyRedirectOnDeletion(snapshotDto: WorkbookSnapshotDto) {
  const redirectUrl = getRedirectUrl(snapshotDto);
  redirect(redirectUrl);
}

async function editSnapshotDialog(
  snapshotDto: WorkbookSnapshotDto,
  tableProvider: () => ResponsiveTable<WorkbookSnapshotDto>,
) {
  const { EditWorkbookSnapshotDialog } =
    await import('cadenza/workbook/header/edit-workbook-snapshot-dialog/edit-workbook-snapshot-dialog');
  const workbookSnapshotData = await new EditWorkbookSnapshotDialog(snapshotDto).connectAndShow();
  if (Object.keys(workbookSnapshotData).length !== 0) {
    try {
      await updateWorkbookSnapshot(snapshotDto.workbookId, workbookSnapshotData);
      if (workbookSnapshotData.printName || workbookSnapshotData.description) {
        getStore().dispatch(updateWorkbookPrintName(workbookSnapshotData.printName));
        tableProvider().refresh();
      }
    } catch {
      showToastMessage(i18n('action.editSnapshot.failure', { workbookPrintName: snapshotDto.printName }));
    }
  }
}

function redirect(redirectUrl: string | undefined) {
  if (redirectUrl) {
    window.location.replace(redirectUrl);
  }
}

function getSnapshotUrl(snapshotDto: WorkbookSnapshotDto) {
  return new URL(createPublicWorkbookUrl(snapshotDto.workbookId, snapshotDto.worksheetId), location.origin);
}

// redirect to parent workbook if currently open snapshot is deleted
function getRedirectUrl(snapshotDto: WorkbookSnapshotDto) {
  return isCurrentWorkbook(snapshotDto.workbookId) ?
      createPublicWorkbookUrl(getWorkbookRelation(getStore().getState())!.parent)
    : undefined;
}

function isCurrentWorkbook(snapshot: WorkbookId) {
  const currentWorkbookId = getWorkbookId(getStore().getState());
  return (
    currentWorkbookId.repositoryName === snapshot.repositoryName && currentWorkbookId.itemName === snapshot.itemName
  );
}

function stringToDate(dateAsString?: string) {
  if (dateAsString) {
    return new Date(dateAsString);
  }
}
